import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Save, Trash2, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const EMPTY_SECTION = { heading: "", body: "", image: "" };

const IMG_STYLES = [
  { id: "botanical", label: "Botanical" },
  { id: "photographic", label: "Photographic" },
  { id: "painterly", label: "Painterly" },
];

const ImageGen = ({ genKey, imgBusy, value, onGenerate, testid }) => (
  <div className="space-y-2">
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-widest text-[#5C6359] mr-1">{value ? "Regenerate:" : "Generate:"}</span>
      {IMG_STYLES.map((st) => (
        <button key={st.id} type="button" onClick={() => onGenerate(st.id)} disabled={!!imgBusy}
          data-testid={`${testid}-${st.id}`}
          className="btn-secondary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> {imgBusy === `${genKey}:${st.id}` ? "Painting…" : st.label}
        </button>
      ))}
    </div>
    {value && <img src={value} alt="preview" data-testid={`${testid}-preview`} className="w-full h-40 object-cover rounded-xl border border-[#D1C7B8]" />}
  </div>
);

const Field = ({ label, value, onChange, multiline, rows = 4, testid, placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">{label}</label>
    {multiline ? (
      <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B]" />
    ) : (
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B]" />
    )}
  </div>
);

export default function AdminCaring() {
  const { user } = useAuth();
  const [hero, setHero] = useState({ eyebrow: "", title: "", subtitle: "", image: "" });
  const [sections, setSections] = useState([]);
  const [themes, setThemes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(null);
  const [copyBusy, setCopyBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/caring-for-country");
    setHero(data.hero || { eyebrow: "", title: "", subtitle: "", image: "" });
    setSections((data.sections || []).map((s) => ({ ...s, _k: crypto.randomUUID() })));
    setThemes(data.theme_categories || []);
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user]); // eslint-disable-line

  const setSection = (i, key, val) => setSections((s) => s.map((sec, idx) => (idx === i ? { ...sec, [key]: val } : sec)));
  const addSection = () => setSections((s) => [...s, { ...EMPTY_SECTION, _k: crypto.randomUUID() }]);
  const removeSection = (i) => setSections((s) => s.filter((_, idx) => idx !== i));

  const genCopy = async (key, kind, topic, apply) => {
    setCopyBusy(key);
    try {
      const r = await api.post("/admin/caring/draft-copy", { kind, topic: topic || null });
      apply(r.data);
      toast.success("Massey drafted the copy — refine it, then Save.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft copy");
    } finally {
      setCopyBusy(null);
    }
  };

  const genImage = async (key, title, apply, style = "botanical") => {
    if (!title?.trim()) { toast.error("Add some text first so Massey knows what to paint"); return; }
    setImgBusy(`${key}:${style}`);
    try {
      const r = await api.post("/admin/library/generate-image", { title, category: "Caring for Country", style });
      apply(r.data.url);
      toast.success("Massey painted an image.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate image");
    } finally {
      setImgBusy(null);
    }
  };

  const save = async () => {
    if (!hero.title?.trim()) { toast.error("Hero title is required"); return; }
    setSaving(true);
    try {
      await api.put("/admin/caring-for-country", { hero, sections: sections.map(({ _k, ...rest }) => rest) });
      toast.success("Caring for Country updated — live now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-caring-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="label-eyebrow mb-2">Admin · Caring for Country</p>
            <h1 className="font-serif text-4xl text-[#2D322B] leading-tight">Tend the hub.</h1>
          </div>
          <Link to="/caring-for-country" target="_blank" className="text-xs uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B] inline-flex items-center gap-1.5" data-testid="caring-view-live">
            View live <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
        <p className="mt-4 text-[#5C6359] max-w-2xl text-sm">
          Edit the hero and story sections of the members' Caring for Country hub. Themed Library articles
          {themes.length ? ` (${themes.join(", ")})` : ""} are pulled in automatically — write those in the Library tab.
          Edits are live the moment you save.
        </p>

        {/* HERO */}
        <section className="mt-8 bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6 space-y-4" data-testid="caring-hero-editor">
          <div className="flex items-center justify-between">
            <p className="label-eyebrow">Hero</p>
            <button type="button" onClick={() => genCopy("hero", "hero", hero.title || hero.eyebrow, (d) => setHero({ ...hero, title: d.title, subtitle: d.subtitle }))}
              disabled={copyBusy === "hero"} data-testid="draft-hero-copy"
              className="btn-secondary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> {copyBusy === "hero" ? "Writing…" : "Draft with Massey"}
            </button>
          </div>
          <Field label="Eyebrow" value={hero.eyebrow} onChange={(v) => setHero({ ...hero, eyebrow: v })} testid="hero-eyebrow" />
          <Field label="Title" value={hero.title} onChange={(v) => setHero({ ...hero, title: v })} testid="hero-title" />
          <Field label="Subtitle" value={hero.subtitle} onChange={(v) => setHero({ ...hero, subtitle: v })} testid="hero-subtitle" multiline rows={3} />
          <Field label="Hero image URL" value={hero.image} onChange={(v) => setHero({ ...hero, image: v })} testid="hero-image" />
          <ImageGen genKey="hero" imgBusy={imgBusy} value={hero.image} testid="gen-hero-image"
            onGenerate={(style) => genImage("hero", hero.title || hero.eyebrow, (u) => setHero({ ...hero, image: u }), style)} />
        </section>

        {/* SECTIONS */}
        <section className="mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <p className="label-eyebrow">{sections.length} story section{sections.length === 1 ? "" : "s"}</p>
            <button onClick={addSection} className="btn-secondary !py-2 !px-5 inline-flex items-center justify-center gap-2" data-testid="add-section">
              <Plus className="w-4 h-4" /> Add section
            </button>
          </div>
          <div className="space-y-4">
            {sections.map((s, i) => (
              <div key={s._k || i} className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6 space-y-4" data-testid={`section-editor-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="label-eyebrow">Section {i + 1}</p>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => genCopy(`sec-${i}`, "section", s.heading, (d) => setSections((arr) => arr.map((sec, idx) => idx === i ? { ...sec, heading: d.heading, body: d.body } : sec)))}
                      disabled={copyBusy === `sec-${i}`} data-testid={`draft-section-copy-${i}`}
                      className="btn-secondary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" /> {copyBusy === `sec-${i}` ? "Writing…" : "Draft with Massey"}
                    </button>
                    <button onClick={() => removeSection(i)} className="text-[#8F3A3A] hover:text-[#C05C42]" data-testid={`remove-section-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <Field label="Heading" value={s.heading} onChange={(v) => setSection(i, "heading", v)} testid={`section-heading-${i}`} />
                <Field label="Body" value={s.body} onChange={(v) => setSection(i, "body", v)} testid={`section-body-${i}`} multiline rows={5} />
                <Field label="Image URL (optional)" value={s.image} onChange={(v) => setSection(i, "image", v)} testid={`section-image-${i}`} />
                <ImageGen genKey={`sec-${i}`} imgBusy={imgBusy} value={s.image} testid={`gen-section-image-${i}`}
                  onGenerate={(style) => genImage(`sec-${i}`, s.heading, (u) => setSection(i, "image", u), style)} />
              </div>
            ))}
            {sections.length === 0 && (
              <p className="text-sm text-[#5C6359] bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6">No sections yet. Add one above.</p>
            )}
          </div>
        </section>

        <div className="mt-8">
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="caring-save">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
